import { zodResolver } from "@hookform/resolvers/zod";
import capitalize from "lodash/capitalize";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import {
  Button,
  ErrorMessage,
  Field,
  Header,
  Label,
  SelectInput,
  Tooltip,
  useAlert,
} from "@web-app/components";
import { Order } from "@web-app/types";
import http from "@web-app/utils/http";

const doordashFormSchema = z.object({
  dasher_allowed_vehicles: z.enum(["car", "bicycle", "both"], {
    required_error: "Please select an allowed vehicle type",
  }),
  deliverableAction: z.enum(["normal", "contactless_dropoff"], {
    required_error: "Please select a deliverable action",
  }),
  // Numeric value as string (e.g., "5.50")
  tip: z
    .string()
    .trim()
    .regex(/^\d*\.?\d{0,2}$/, "Please enter a valid amount (e.g., 5.50)")
    .optional(),
  undeliverableAction: z.enum(["return_to_pickup", "dispose"], {
    required_error: "Please select an undeliverable action",
  }),
});

type DoordashFormData = z.infer<typeof doordashFormSchema>;

type DoordashQuotation = {
  external_delivery_id?: string;
  fee?: number;
  tip?: number;
};

export function OrderDoordashDetails({
  onClose,
  order,
}: {
  onClose: () => void;
  order: Order;
}) {
  const { showAlertAsync } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [quote, setQuote] = useState<DoordashQuotation>({});
  const [isQuotationFetched, setIsQuotationFetched] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string[]>([]);

  const { control, handleSubmit } = useForm<DoordashFormData>({
    defaultValues: {
      dasher_allowed_vehicles: "car",
      deliverableAction: "normal",
      undeliverableAction: "return_to_pickup",
    },
    mode: "onChange",
    resolver: zodResolver(doordashFormSchema),
  });

  const getDeliverableActionOptions = () => {
    return [
      {
        label: "Normal",
        value: "normal",
      },
      {
        label: "Contactless Dropoff",
        value: "contactless_dropoff",
      },
    ];
  };

  const getUndeliverableActionOptions = () => {
    return [
      {
        label: "Return to pickup",
        value: "return_to_pickup",
      },
      {
        label: "Dispose",
        value: "dispose",
      },
    ];
  };

  const getDasherAllowedVehiclesOptions = () => {
    return [
      {
        label: "Car",
        value: "car",
      },
      {
        label: "Bicycle",
        value: "bicycle",
      },
      {
        label: "Both",
        value: "both",
      },
    ];
  };

  const getDoordashQuotation = async (formData: DoordashFormData) => {
    setIsLoading(true);
    setErrorMessage([]);
    await showAlertAsync(
      http.post(`/integrations/doordash/quote?orderId=${order.id}`, {
        dasher_allowed_vehicles: formData.dasher_allowed_vehicles,
        deliverableAction: formData.deliverableAction,
        undeliverableAction: formData.undeliverableAction,
      }),
      {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error: (err: any) => {
          const errData = err.response?.data?.data;
          const errList: string[] = [];
          errList.push(errData?.message);
          errData?.field_errors?.map(
            (fieldErr: { error: string; field: string }) => {
              errList.push(`${fieldErr.field}: ${capitalize(fieldErr.error)}`);
            },
          );
          setErrorMessage(errList);
          setIsLoading(false);
          return {
            message: "Failed to fetch Doordash quotation",
            title: "Error",
            type: "error",
          };
        },
        loading: {
          message: "Fetching Doordash quotation...",
          title: "Fetching",
          type: "default",
        },
        success: {
          message: "Doordash quotation fetched",
          title: "Success",
          type: "success",
        },
      },
    ).then((response) => {
      setQuote(response.data.data);
      setIsQuotationFetched(true);
      setIsLoading(false);
    });
  };

  const handleAcceptQuote = async () => {
    const response = await showAlertAsync(
      http.post(
        `/integrations/doordash/quote/${quote.external_delivery_id}/accept?orderId=${order.id}`,
      ),
      {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error: (err: any) => {
          const errData = err.response?.data?.data;
          const errList: string[] = [];
          errList.push(errData?.message);
          errData?.field_errors?.map(
            (fieldErr: { error: string; field: string }) => {
              errList.push(`${fieldErr.field}: ${capitalize(fieldErr.error)}`);
            },
          );
          setErrorMessage(errList);
          setIsLoading(false);
          return {
            message: "Failed to fetch Doordash quotation",
            title: "Error",
            type: "error",
          };
        },
        loading: {
          message: "Sending order to Doordash...",
          title: "Sending",
          type: "default",
        },
        success: {
          message: "Order sent to Doordash",
          title: "Success",
          type: "success",
        },
      },
    );

    if (response.status === 200) {
      onClose();
    }
  };

  return (
    <div className="absolute bg-white h-full w-full z-30 flex flex-col">
      {/* Sticky Header */}
      <Header
        title="Send Order to Doordash"
        leftActions={
          <Button
            icon="fasChevronLeft"
            onClick={onClose}
            plain
            className="!p-0 hover:!bg-white"
          />
        }
      />

      {errorMessage.length > 0 &&
        errorMessage.map((msg, index) => (
          <div className="px-4">
            <ErrorMessage key={index} message={msg} />
          </div>
        ))}

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {isQuotationFetched && (
          <div className="w-full bg-white rounded-md space-y-2">
            <div className="grid grid-cols-3 gap-4 pb-2">
              <div className="col-span-1 flex items-center">
                <Label className="text-sm font-medium text-gray-700">
                  Cost
                </Label>
              </div>
              <div className="col-span-2">
                <div className="text-sm font-medium text-gray-700">
                  {`$${((quote?.fee || 0) / 100).toFixed(2)}`}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 pb-2">
              <div className="col-span-1 flex items-center">
                <Label className="text-sm font-medium text-gray-700">Tip</Label>
              </div>
              <div className="col-span-2">
                <div className="text-sm font-medium text-gray-700">
                  {`$${((quote?.tip || 0) / 100).toFixed(2)}`}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 pb-2">
              <div className="col-span-1 flex items-center">
                <Label className="text-sm font-medium text-gray-700">
                  External Delivery ID
                </Label>
              </div>
              <div className="col-span-2">
                <div className="text-sm font-medium text-gray-700">
                  {`${quote?.external_delivery_id || "-"}`}
                </div>
              </div>
            </div>
          </div>
        )}

        {!isQuotationFetched && (
          <div className="w-full bg-white rounded-md">
            <div className="flex flex-col gap-2">
              <Field className="w-full">
                <Label>Deliverable Action</Label>
                <Controller
                  name="deliverableAction"
                  control={control}
                  rules={{
                    required: "Deliverable action is required",
                  }}
                  render={({ field }) => (
                    <SelectInput
                      options={getDeliverableActionOptions()}
                      name="deliverable_action"
                      selected={field.value}
                      onChange={(value: string) => {
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </Field>

              <Field className="w-full">
                <Label>Undeliverable Action</Label>
                <Controller
                  name="undeliverableAction"
                  control={control}
                  rules={{
                    required: "Undeliverable action is required",
                  }}
                  render={({ field }) => (
                    <SelectInput
                      options={getUndeliverableActionOptions()}
                      name="undeliverable_action"
                      selected={field.value}
                      onChange={(value: string) => {
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </Field>

              <Field className="w-full">
                <Label>Allowed vehicle type</Label>
                <Controller
                  name="dasher_allowed_vehicles"
                  control={control}
                  rules={{
                    required: "Allowed vehicle type is required",
                  }}
                  render={({ field }) => (
                    <SelectInput
                      options={getDasherAllowedVehiclesOptions()}
                      name="dasher_allowed_vehicles"
                      selected={field.value}
                      onChange={(value: string) => {
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </Field>
            </div>
          </div>
        )}
      </div>

      {/* Sticky Footer */}
      <div className="border-t border-gray-200 p-4 bg-white">
        <div className="flex gap-2">
          {isQuotationFetched && (
            <Tooltip text="Accept quote to send order to Doordash">
              <Button
                type="button"
                isLoading={isLoading}
                onClick={() => handleAcceptQuote()}
              >
                Accept Quote
              </Button>
            </Tooltip>
          )}
          {!isQuotationFetched && (
            <Tooltip text="Request quote to see the estimated cost">
              <Button
                type="button"
                isLoading={isLoading}
                onClick={handleSubmit(getDoordashQuotation)}
              >
                Request Quote
              </Button>
            </Tooltip>
          )}
          <Button type="button" plain onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
